/**
 * Client-side helpers for shareable invite links.
 *
 * See `InviteLinkDoc` in ./types.ts and the tripInvites{} rules in
 * firestore.rules for the full contract. Two of the four write actions
 * (create, revoke) are ordinary Firestore writes gated by rules; redeeming
 * is a callable Cloud Function because the accept-count increment has to be
 * transactional against a race between simultaneous joiners.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { getFirebaseFunctions, getFirestoreDb } from "./firebase";
import { toResult, type DbResult } from "./result";
import type {
  InviteLink,
  InviteLinkCreate,
  InviteLinkDoc,
  TravelerRole,
  Trip,
} from "./types";

/** Firestore collection name for invite tokens. */
const COLLECTION = "tripInvites";

/**
 * URL-safe alphabet for tokens — base32 with an easy-to-read subset, minus
 * lookalikes (0/O/1/I). 12 chars → 32^12 ≈ 10^18 combinations, unguessable.
 */
const TOKEN_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
const TOKEN_LENGTH = 12;

/**
 * Generate a random, URL-safe token. Uses the platform's crypto RNG when
 * available and falls back to Math.random on very old runtimes.
 */
function generateInviteToken(): string {
  const bytes = new Uint8Array(TOKEN_LENGTH);
  const globalCrypto =
    typeof crypto !== "undefined" ? crypto : undefined;
  if (globalCrypto?.getRandomValues) {
    globalCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += TOKEN_ALPHABET.charAt(bytes[i]! % TOKEN_ALPHABET.length);
  }
  return out;
}

// ─── Reads ───────────────────────────────────────────────────────────────────

/** Fetch a single invite link by token. */
export async function getInviteLink(
  token: string,
): Promise<DbResult<InviteLink>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const ref = doc(db, COLLECTION, token);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      throw new Error("Invite link not found");
    }
    return { id: snap.id, ...(snap.data() as InviteLinkDoc) };
  });
}

/**
 * List all invite links for one trip, newest first.
 * The trip-settings UI reads this for the "Share invite link" panel.
 */
export async function listInviteLinksForTrip(
  tripId: string,
): Promise<DbResult<InviteLink[]>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    const q = query(
      collection(db, COLLECTION),
      where("tripId", "==", tripId),
      orderBy("createdAt", "desc"),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as InviteLinkDoc),
      }));
  });
}

// ─── Writes ──────────────────────────────────────────────────────────────────

/**
 * Create a new invite link for a trip. The caller must already be a member of
 * the trip (enforced in Firestore rules).
 *
 * @param trip           the full trip doc — we denormalize display fields off it
 * @param creator        the current user (uid + email); creator's role is
 *                       recorded so the rule can prove they're a member.
 * @param options.role   role assigned to redeemers. "owner" is rejected.
 * @param options.expiresAt   optional wall-clock expiration
 * @param options.maxAccepts  optional seat cap; null = unlimited
 */
export async function createInviteLink(
  trip: Trip,
  creator: { uid: string; email: string },
  options: {
    role: TravelerRole;
    expiresAt: Date | null;
    maxAccepts: number | null;
  },
): Promise<DbResult<InviteLink>> {
  return toResult(async () => {
    if (options.role === "owner") {
      throw new Error("Invite links can't grant the owner role.");
    }
    const db = getFirestoreDb();
    // Retry on the astronomically unlikely token collision.
    let token = generateInviteToken();
    for (let attempt = 0; attempt < 3; attempt++) {
      const existing = await getDoc(doc(db, COLLECTION, token));
      if (!existing.exists()) break;
      token = generateInviteToken();
    }

    const record: InviteLinkCreate = {
      tripId: trip.id,
      role: options.role,
      createdBy: creator.uid,
      createdByEmail: creator.email.toLowerCase(),
      createdAt: Timestamp.now(),
      expiresAt: options.expiresAt ? Timestamp.fromDate(options.expiresAt) : null,
      maxAccepts: options.maxAccepts,
      acceptCount: 0,
      revoked: false,
      tripName: trip.name,
      city: trip.destinations[0] ?? "",
      miniature: trip.coverImage ?? null,
      startDate: trip.startDate,
      endDate: trip.endDate,
    };

    // The Firestore rule for tripInvites/{token} does the parent-trip
    // membership check via a nested get() — a standard client write is
    // sufficient. See firestore.rules § tripInvites.
    await setDoc(doc(db, COLLECTION, token), record);

    return { id: token, ...(record as InviteLinkDoc) };
  });
}

/**
 * Mark an invite link as revoked. Only the creator or a trip owner can revoke
 * (enforced in Firestore rules). Revocation is preferred over deletion so the
 * accept page can render a friendly "this link was revoked" message.
 */
export async function revokeInviteLink(
  token: string,
): Promise<DbResult<void>> {
  return toResult(async () => {
    const db = getFirestoreDb();
    await updateDoc(doc(db, COLLECTION, token), {
      revoked: true,
      revokedAt: serverTimestamp(),
    });
  });
}

// ─── Redeem — callable Cloud Function ────────────────────────────────────────

export interface RedeemInviteLinkResult {
  tripId: string;
}

/**
 * Redeem an invite link. This calls the `redeemInviteLink` HTTPS callable so
 * the server can validate + increment the counter + add the user as a member,
 * all in one Firestore transaction — no race between simultaneous joiners.
 *
 * The function reads the caller's uid/email/name from the auth context, so the
 * client only needs to pass the token.
 */
export async function redeemInviteLink(
  token: string,
): Promise<DbResult<RedeemInviteLinkResult>> {
  return toResult(async () => {
    const fn = httpsCallable<{ token: string }, RedeemInviteLinkResult>(
      getFirebaseFunctions(),
      "redeemInviteLink",
    );
    const result = await fn({ token });
    return result.data;
  });
}
